import { memo, useState, useEffect, useMemo } from 'react';
import { LABEL_STYLE, fetchTopoJSON, getTopoCache, geoToISO3 } from '../shared/constants';
import { InnovationTooltip } from '../shared/InnovationTooltip';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList,
} from 'recharts';
import {
  ComposableMap, Geographies, Geography, ZoomableGroup,
} from 'react-simple-maps';
import { ChartWrapper } from '../shared/ChartWrapper';
import { NoChartData } from '../shared/NoChartData';
import { useResponsiveYAxisWidth } from '../shared/useResponsiveYAxisWidth';
import { useInnovationData } from './useInnovationData';
import { isGlobalSouthISO3 } from '../../../utils/countryMapping';

export const InnovationGeographyChart = memo(function InnovationGeographyChart() {
  const { geographyData, totalCount, innovationResults } = useInnovationData();
  const countryYAxisWidth = useResponsiveYAxisWidth(110);
  const regionYAxisWidth = useResponsiveYAxisWidth(130);

  // Map state
  const [topology, setTopology] = useState(getTopoCache());
  const [tip, setTip] = useState('');
  const [tipPos, setTipPos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (!topology) fetchTopoJSON().then(setTopology);
  }, [topology]);

  const rawIso3Counts = geographyData?.iso3Counts || {};
  const iso3Counts = useMemo(() => {
    const filtered = {};
    for (const [iso3, count] of Object.entries(rawIso3Counts)) {
      if (isGlobalSouthISO3(iso3)) filtered[iso3] = count;
    }
    return filtered;
  }, [rawIso3Counts]);

  const maxCount = useMemo(() => {
    const vals = Object.values(iso3Counts);
    return vals.length > 0 ? Math.max(...vals) : 1;
  }, [iso3Counts]);

  const highlightedISO3 = useMemo(() => new Set(Object.keys(iso3Counts)), [iso3Counts]);

  const getColor = (iso3) => {
    if (!highlightedISO3.has(iso3)) return '#E5E7EB';
    const count = iso3Counts[iso3] || 1;
    const t = maxCount > 1 ? (count - 1) / (maxCount - 1) : 0;
    const r = Math.round(191 + t * (0 - 191));
    const g = Math.round(219 + t * (125 - 219));
    const b = Math.round(254 + t * (188 - 254));
    return `rgb(${r},${g},${b})`;
  };

  if (!geographyData || geographyData.topCountries.length === 0) return (
    <ChartWrapper title="Innovation Geography">
      <NoChartData message="No geographic data on innovation results" detail="This chart maps innovation results by country and region using the Countries and Regions fields. None of the innovation results in the current selection have geographic data assigned." />
    </ChartWrapper>
  );

  const { topCountries, topRegions } = geographyData;

  const countryData = topCountries.slice(0, 15).map(([name, count]) => ({ name, count }));
  const regionData = topRegions.map(([name, count]) => ({ name, count }));

  const exportData = [
    ...topCountries.map(([name, count]) => ({ Geography: name, Type: 'Country', 'Innovation Results': count })),
    ...topRegions.map(([name, count]) => ({ Geography: name, Type: 'Region', 'Innovation Results': count })),
  ];

  const uniqueCountries = Object.keys(geographyData.countryCounts).length;

  return (
    <ChartWrapper title="Innovation Results by Geography" info="Geographic distribution of innovation results by country and region." chartData={exportData} exportFilename="innovation-geography" sourceResults={innovationResults}>
      <div className="mb-3 text-center">
        <span className="text-sm text-gray-600">
          Innovation results reported across <strong>{uniqueCountries}</strong> countries and <strong>{topRegions.length}</strong> regions
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <h5 className="text-sm font-medium text-gray-700 mb-2">Top 15 Countries</h5>
          <ResponsiveContainer width="100%" height={Math.max(250, countryData.length * 24)}>
            <BarChart data={countryData} layout="vertical" margin={{ left: 10, right: 30 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" tick={{ fontSize: 12 }} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: countryYAxisWidth < 110 ? 9 : 10 }} width={countryYAxisWidth} />
              <Tooltip content={<InnovationTooltip unit="innovations" />} />
              <Bar dataKey="count" fill="#007DBC" radius={[0, 4, 4, 0]}>
                <LabelList dataKey="count" position="right" style={LABEL_STYLE} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div>
          <h5 className="text-sm font-medium text-gray-700 mb-2">By Region</h5>
          <ResponsiveContainer width="100%" height={Math.max(250, regionData.length * 28)}>
            <BarChart data={regionData} layout="vertical" margin={{ left: 10, right: 30 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" tick={{ fontSize: 12 }} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: regionYAxisWidth < 130 ? 9 : 10 }} width={regionYAxisWidth} />
              <Tooltip content={<InnovationTooltip unit="innovations" />} />
              <Bar dataKey="count" fill="#16a34a" radius={[0, 4, 4, 0]}>
                <LabelList dataKey="count" position="right" style={LABEL_STYLE} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* World map */}
      {topology && Object.keys(iso3Counts).length > 0 && (
        <div className="mt-6">
          <h5 className="text-sm font-medium text-gray-700 mb-2">Innovation Results Map</h5>
          <div className="relative aspect-[16/7]" onMouseMove={(e) => { if (tip) setTipPos({ x: e.clientX, y: e.clientY }); }}>
            <ComposableMap projection="geoEquirectangular" projectionConfig={{ rotate: [-10, 0, 0], scale: 130 }} width={800} height={350} style={{ width: '100%', height: '100%' }}>
              <ZoomableGroup>
                <Geographies geography={topology}>
                  {({ geographies }) =>
                    geographies.map((geo) => {
                      const iso3 = geoToISO3(geo);
                      const isHighlighted = highlightedISO3.has(iso3);
                      const count = iso3Counts[iso3];
                      return (
                        <Geography
                          key={geo.rsmKey}
                          geography={geo}
                          fill={getColor(iso3)}
                          stroke="#fff"
                          strokeWidth={0.5}
                          onMouseEnter={(evt) => {
                            const name = geo.properties.NAME || geo.properties.name || iso3;
                            setTip(isHighlighted ? `${name}: ${count} innovation${count !== 1 ? 's' : ''}` : name);
                            setTipPos({ x: evt.clientX, y: evt.clientY });
                          }}
                          onMouseLeave={() => setTip('')}
                          style={{
                            default: { outline: 'none' },
                            hover: { fill: isHighlighted ? '#003F6B' : '#D1D5DB', outline: 'none', cursor: 'pointer' },
                            pressed: { outline: 'none' },
                          }}
                        />
                      );
                    })
                  }
                </Geographies>
              </ZoomableGroup>
            </ComposableMap>
            {tip && (
              <div className="fixed z-50 px-3 py-1.5 bg-gray-900 text-white text-xs rounded-lg shadow-lg pointer-events-none" style={{ left: tipPos.x + 12, top: tipPos.y - 28 }}>
                {tip}
              </div>
            )}
          </div>
          <div className="mt-2 flex items-center justify-center gap-2 text-xs text-gray-500">
            <span>Fewer innovations</span>
            <div className="w-24 h-3 rounded" style={{ background: 'linear-gradient(to right, #BFDBFE, #007DBC)' }} />
            <span>More innovations</span>
          </div>
        </div>
      )}
    </ChartWrapper>
  );
});
